import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { SENDER_TYPE, PriorityPlaylist, ContentNugget } from '@backrs_ui/common-ui';

import type {
  AttachedNugget,
  ChatAttachment,
} from '@backrs_ui/common-ui/src/molecules/Lubav/InputBox';
import { Message } from '@backrs_ui/common-ui/src/molecules/Lubav/Message';
import { UploadedFile } from '@backrs_ui/common-ui/src/molecules/Profile/UploadedFile';
import { useAgentContext } from '@backrs_ui/grow-ui/src/agentContext/AgentContextProvider';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ChatBoxContainer } from '@backrs_ui/grow-ui/src/components/Lubav/ChatBoxContainer';
import { FileUpload } from '@backrs_ui/grow-ui/src/components/Lubav/FileUpload';
import {
  getCoachingUserName,
  getFileExtensionFromMimeType,
} from '@backrs_ui/grow-ui/src/components/Lubav/fileUpload.util';
import { useMessageActions } from '@backrs_ui/grow-ui/src/components/Lubav/hooks/useMessageActions';
import { AIMessageActionsButton } from '@backrs_ui/grow-ui/src/components/Lubav/MessageActionButtons';
import { SelectedUser } from '@backrs_ui/grow-ui/src/components/Lubav/SelectedUser';
import {
  MetaMessage,
  ToolMessage,
  NuggetMessage,
  DoneMessageWithIds,
  ComponentMessage,
  FeaturedTypeMessage,
  ArtifactMessage,
  ChartMessage,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { SuggestedPrompts } from '@backrs_ui/grow-ui/src/components/Lubav/SuggestedPrompts';
import { UserSearch, UserSelect } from '@backrs_ui/grow-ui/src/components/Lubav/UserSearch';
import { getShareUrl } from '@backrs_ui/grow-ui/src/components/Nuggets/nuggetLinks';
import { BoxType } from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/InfoBox.config';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  ChartSpec,
  DraggableNuggetData,
  ResourcesContent,
  OfficeHoursMessage,
  DocumentType,
  DOCUMENT_NAMES,
  DOCUMENT_TYPES,
  LubavSessionType,
  LubavOfficeHoursSubsessionType,
  OFFICE_HOURS_SUBSESSION_IDS,
  ProfileItemType,
  LUBAV_SUPERSESSIONS,
  OFFICE_HOURS_SESSION_IDS,
  ResourceKey,
  STRUCTURED_SESSION_IDS,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import { AttachedNuggetChip } from '@backrs_ui/grow-ui/src/pages/OfficeHours/molecules/AttachedNuggetChip';
import { openSignupPrompt } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import {
  setIsLoadingResources,
  setCurrentResponseId,
  setResourcesContent,
  setToolTriggered,
  setHighlightedNugget,
  applyArtifactDelta,
  setArtifact,
  setArtifactStreaming,
  resetBoxData,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  addOfficeHoursMessage,
  setOfficeHoursMessages,
  updateLastMessage,
  setSuggestedPrompts,
  resetSuggestedPrompts,
  setSessionId,
  setIsLoading,
  setIsStreaming,
  setInitialMessageFetched,
  addActiveTool,
  removeActiveTool,
  setPendingSelectorResponse,
  setSessionTitle,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import type { AttachedFilePayload } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import {
  useGetAttachmentUrlMutation,
  useSendMessageMutation,
  useGetInitialMessageMutation,
  useGetNewSessionIdForMessageEditMutation,
  useAppendMessageMutation,
  officeHoursApi,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import {
  initiateSession,
  resetStreamingState,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { applySessionSwitch } from '@backrs_ui/grow-ui/src/redux/coachLubav/sessionSwitch.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  clearQueuedPrompt,
  closeMobileFab,
  setPreviewedSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  getActiveLubavSession,
  getActiveLubavSubsession,
  getLoadingSessionData,
  getPendingStructuredChoice,
  getPreviewedSession,
  getSessionState,
  createSessionAction,
  isShellManagedSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  selectPagePrompts,
  type PagePrompt,
} from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { openSuggestedPlaylistViewStep } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { setSessionStatus } from '@backrs_ui/grow-ui/src/redux/sessionStatuses/sessionStatuses';
import {
  selectIsSharedSessionActive,
  selectSharedResourceType,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { openCustomTab, setSelectedTabKey } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';
import {
  selectUserDocuments,
  selectUserId,
  selectUser,
} from '@backrs_ui/grow-ui/src/redux/user/user';
import {
  transformIdsToResourcesContent,
  hydratedDataToContentNugget,
  nuggetTypeToResourceKey,
  resourceKeyToTab,
} from '@backrs_ui/grow-ui/src/utils/streamUtils';
import { uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import { Typography, Box, SxProps, Theme, Button, Snackbar, useMediaQuery } from '@mui/material';

import { CareerPathSelector } from './CareerPathSelector';
import { ChatSkeleton } from './ChatSkeleton';
import { CollegeTypeSelector } from './CollegeTypeSelector';
import { MessageCharts } from './MessageCharts';
import {
  COACHING_LEAF_DOC_REQUIREMENTS,
  getOfficeHoursPromptOptions,
  shouldShowPromptsInMessage as getShouldShowPromptsInMessage,
  getPromptVariant,
} from './officeHoursPrompts';
import { PromptButtons, PromptOption } from './PromptButtons';
import { StructuredQuestionInput } from './StructuredQuestionInput';
import { useLubavLaunchParams } from './useLubavLaunchParams';

const { chatBox } = appStrings.modules;

const ARTIFACT_GENERATING_TOOLS = new Set([
  'generate-output',
  'generate-college-shortlist',
  'generate-study-plan',
]);

// Hand-maintained mirror of the agent's tools whose results become cards in the
// right-hand panel, beyond the search-* and *-subsession families.
const NUGGET_TOOLS = new Set(['find-by-name', 'query-college-data', 'get-suggested-playbooks']);

// Whether the right-hand panel should show its loader while a tool runs. Only tools
// whose output lands there qualify; everything else answers in the chat.
const fillsInfoBox = (toolName: string) =>
  NUGGET_TOOLS.has(toolName) ||
  ARTIFACT_GENERATING_TOOLS.has(toolName) ||
  toolName.startsWith('search-') ||
  toolName.endsWith('-subsession');

type ChatBoxProps = Readonly<{
  superuser: boolean;
  supercoach: boolean;
}>;

const isOfficeHoursSubsessionType = (
  sessionId: LubavSessionType | undefined,
): sessionId is LubavOfficeHoursSubsessionType => {
  if (!sessionId) {
    return false;
  }

  return Object.values(OFFICE_HOURS_SUBSESSION_IDS).includes(
    sessionId as LubavOfficeHoursSubsessionType,
  );
};

export type SendMessageParams = Readonly<{
  message: string;
  LLMInput?: string;
  currentMessageList?: OfficeHoursMessage[];
  newSessionId?: number;
  requiresDocument?: DocumentType[];
  confirmedDocuments?: DocumentType[];
  allowSkipDocument?: boolean;
  includeProfileItems?: ProfileItemType[];
  officeHoursSubsessionType?: LubavOfficeHoursSubsessionType;
  attachedNuggets?: AttachedNugget[];
  attachedFiles?: AttachedFilePayload[];
  promptButtonId?: string;
}>;

export const ChatBox = ({ superuser, supercoach }: ChatBoxProps) => {
  const dispatch = useAppDispatch();
  const agentCtx = useAgentContext();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const activeSession = useAppSelector(getActiveLubavSession);
  const pendingStructuredChoice = useAppSelector(getPendingStructuredChoice);
  const previewedSession = useAppSelector(getPreviewedSession);
  const loadingSessionData = useAppSelector(getLoadingSessionData);
  const officeHoursSubsessionType = useAppSelector(getActiveLubavSubsession);
  const isShared = useAppSelector(selectIsSharedSessionActive);
  const sharedResourceType = useAppSelector(selectSharedResourceType);
  // Only a session share is a read-only transcript; a blueprint share opens a live chat.
  const isSharedTranscript = isShared && sharedResourceType !== 'blueprint';

  const {
    language,
    openAIModel: LLM,
    officeHoursMessages,
    coachingUser,
    suggestedPrompts,
    loading: isTyping,
    streaming: isStreaming,
    initialMessageFetched,
    activeTools,
  } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const resourcesContent = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData.boxData.resources,
  );
  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const isGuidanceCounselor = useAppSelector((state) => state.user.isGuidanceCounselor);
  const documents = useAppSelector(selectUserDocuments);
  const user = useAppSelector(selectUser);
  const userFirstName = user?.first_name ?? '';
  const { mostRecentUserResponseSet, loadingMostRecentUserResponseSet } = useAppSelector(
    (state) => state.userResponseSet,
  );
  const hasCompletedBlueprint = !!mostRecentUserResponseSet?.user_responses;

  const userId = useAppSelector(selectUserId);
  const { isGuest, isUserOrGuest, loggedIn } = useAuth();
  const pagePrompts = useAppSelector(selectPagePrompts);

  const pendingDocReqRef = useRef<{
    llmInput: string;
    promptButtonId?: string;
    requiredDocuments: DocumentType[];
    allowSkipDocument: boolean;
    confirmedDocuments: DocumentType[];
  } | null>(null);

  const [attachedNuggets, setAttachedNuggets] = useState<readonly DraggableNuggetData[]>([]);
  const addNugget = useCallback((nugget: DraggableNuggetData) => {
    setAttachedNuggets((prev) => {
      if (prev.some((n) => n.id === nugget.id && n.type === nugget.type)) {
        return prev;
      }

      return [...prev, nugget];
    });
  }, []);

  const removeNugget = useCallback((id: string) => {
    setAttachedNuggets((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<readonly ChatAttachment[]>([]);
  const fileIdCounter = useRef(0);

  const handleFileSelect = useCallback(async (files: File[]) => {
    const pending = files.map((file) => ({
      file,
      tempId: `temp-${fileIdCounter.current++}`,
    }));

    setAttachedFiles((prev) => [
      ...prev,
      ...pending.map(({ file, tempId }) => ({
        s3_key: tempId,
        filename: file.name,
        mime_type: file.type,
        uploadStatus: 'uploading' as const,
      })),
    ]);

    await Promise.all(
      pending.map(async ({ file, tempId }) => {
        try {
          const result = await uploadFile({ file });

          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.s3_key === tempId
                ? { ...f, s3_key: result.s3Key, uploadStatus: 'done' as const }
                : f,
            ),
          );
        } catch (error) {
          console.error('ChatBox: file upload failed', { filename: file.name, error });
          setAttachedFiles((prev) =>
            prev.map((f) => (f.s3_key === tempId ? { ...f, uploadStatus: 'error' as const } : f)),
          );
        }
      }),
    );
  }, []);

  const [fileError, setFileError] = useState<string | null>(null);
  const handleFileValidationError = useCallback((message: string) => {
    setFileError(message);
  }, []);

  const handleFileRemove = useCallback((s3Key: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.s3_key !== s3Key));
  }, []);

  const clearFiles = useCallback(() => setAttachedFiles([]), []);

  const clearNuggets = useCallback(() => {
    setAttachedNuggets([]);
  }, []);

  const [sendMessageMutation] = useSendMessageMutation();
  const [getNewSessionIdForMessageEditMutation] = useGetNewSessionIdForMessageEditMutation();
  const [appendMessageMutation] = useAppendMessageMutation();
  const [getAttachmentUrlMutation] = useGetAttachmentUrlMutation();

  const handleOpenAttachment = useCallback(
    async (s3Key: string) => {
      // The tab has to be opened inside the click itself; browsers block a window
      // opened after the await. Opening it blank also keeps it same-origin long
      // enough to drop the opener before the file loads.
      const tab = window.open('', '_blank');

      if (!tab) {
        setFileError(chatBox.attachmentBlockedErr);

        return;
      }

      tab.opener = null;

      try {
        const { url } = await getAttachmentUrlMutation({ s3_key: s3Key }).unwrap();

        if (!url) {
          throw new Error('attachment url missing');
        }

        tab.location.href = url;
      } catch (error) {
        console.error('ChatBox: failed to open attachment', { s3Key, error });
        tab.close();
        setFileError(chatBox.attachmentOpenErr);
      }
    },
    [getAttachmentUrlMutation],
  );

  const [streamingContent, setStreamingContent] = useState<string>('');
  const [streamingCharts, setStreamingCharts] = useState<ChartSpec[]>([]);
  const messageContentRefs = useRef(new Map<number, HTMLDivElement | null>());
  const streamingContentRef = useRef<string>('');
  const streamingChartsRef = useRef<ChartSpec[]>([]);
  const pendingComponentRef = useRef<ComponentMessage | null>(null);

  const handleDeltaMeta = (meta: MetaMessage) => {
    setStreamingContent('');
    setStreamingCharts([]);
    streamingContentRef.current = '';
    streamingChartsRef.current = [];
    dispatch(createSessionAction(setCurrentResponseId, activeSession)(meta.response_id));
  };

  const handleChart = (message: ChartMessage) => {
    streamingChartsRef.current = [...streamingChartsRef.current, message.spec];
    setStreamingCharts(streamingChartsRef.current);
  };

  const clearStreamingReply = () => {
    streamingContentRef.current = '';
    streamingChartsRef.current = [];
    setStreamingContent('');
    setStreamingCharts([]);
  };

  // What a stopped or abandoned turn has shown so far, kept as a message so it isn't lost.
  const keepPartialReply = useCallback(() => {
    const text = streamingContentRef.current;
    const charts = streamingChartsRef.current;

    if (text.trim() || charts.length) {
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          activeSession,
        )({
          text,
          sender: SENDER_TYPE.AI,
          ...(charts.length ? { charts } : {}),
        }),
      );
    }

    clearStreamingReply();
  }, [activeSession, dispatch]);

  const handleDeltaContent = (accumulated: string) => {
    setStreamingContent(accumulated);
    streamingContentRef.current = accumulated;
  };

  const handleArtifact = (message: ArtifactMessage) => {
    dispatch(createSessionAction(applyArtifactDelta, activeSession)(message));
    dispatch(createSessionAction(setArtifactStreaming, activeSession)(true));
  };

  const handleDeltaTool = (tool: ToolMessage) => {
    if (tool.status === 'started') {
      dispatch(
        createSessionAction(
          addActiveTool,
          activeSession,
        )({
          tool_name: tool.tool_name,
          display_name: tool.display_name,
        }),
      );

      if (fillsInfoBox(tool.tool_name)) {
        dispatch(createSessionAction(setToolTriggered, activeSession)(true));
      }

      // Clear the prior artifact
      if (ARTIFACT_GENERATING_TOOLS.has(tool.tool_name)) {
        dispatch(createSessionAction(setArtifact, activeSession)(undefined));
      }
    } else {
      dispatch(createSessionAction(removeActiveTool, activeSession)(tool.tool_name));
    }
  };

  const handleDeltaComponent = (component: ComponentMessage) => {
    pendingComponentRef.current = component;
  };

  const resourcesContentRef = useRef(resourcesContent);
  const featuredTypesRef = useRef<string[]>([]);

  resourcesContentRef.current = resourcesContent;

  const handleFeaturedType = (message: FeaturedTypeMessage) => {
    featuredTypesRef.current = [...featuredTypesRef.current, message.nugget_type];
  };

  const handleNugget = (nugget: NuggetMessage) => {
    const contentNugget = hydratedDataToContentNugget(nugget.nugget_type, nugget.data);

    if (!contentNugget) return;

    const transformed = transformIdsToResourcesContent(
      {
        nugget_ids: [
          {
            type: nugget.nugget_type,
            id: String(contentNugget.id),
            contentNugget,
          },
        ],
        featured_nugget_types: featuredTypesRef.current,
      },
      resourcesContentRef.current,
      // structured-session ids live in activeSession, not officeHoursSubsessionType (which is
      // only set for office-hours subsessions like high_school_act_sat). Fall back so featured-
      // resource config lookups work for structured sessions.
      officeHoursSubsessionType ?? activeSession,
    );

    resourcesContentRef.current = transformed;
    handleSetResourcesContent(transformed);
  };

  const createDoneHandler = (
    pendingMessage: string,
    messagesToUse: OfficeHoursMessage[],
    attachedNuggets?: AttachedNugget[],
    doneAttachedFiles?: AttachedFilePayload[],
  ) => {
    return (done: DoneMessageWithIds) => {
      // The backend reports the structured session's status with each turn; store it so the
      // switch prompt/skeleton read it without re-querying.
      if (done.session_status && activeSession) {
        dispatch(
          setSessionStatus({
            sessionType: activeSession as StructuredSessionId,
            entry: { status: done.session_status, sessionId: sessionId ?? null },
          }),
        );
      }

      const nuggetIds = done.nugget_ids || [];

      const userComponentType: OfficeHoursMessage['componentType'] = doneAttachedFiles?.length
        ? { type: 'ATTACHED_FILES', attachments: doneAttachedFiles }
        : attachedNuggets?.length
          ? { type: 'ATTACHED_NUGGETS', nuggets: attachedNuggets }
          : undefined;

      const userMessage: OfficeHoursMessage[] =
        (pendingMessage && pendingMessage.trim()) || doneAttachedFiles?.length
          ? [
              {
                text: pendingMessage,
                sender: SENDER_TYPE.USER,
                id: String(done.query_id),
                componentType: userComponentType,
              },
            ]
          : [];

      const componentType: OfficeHoursMessage['componentType'] = (() => {
        if (pendingComponentRef.current?.component_type === 'single_select') {
          return {
            type: 'SINGLE_SELECT' as const,
            options: pendingComponentRef.current.options ?? [],
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
            required: pendingComponentRef.current.required ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'multi_select') {
          return {
            type: 'MULTI_SELECT' as const,
            options: pendingComponentRef.current.options ?? [],
            exclusiveOptions: pendingComponentRef.current.exclusive_options,
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
            required: pendingComponentRef.current.required ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'multi_numeric') {
          return {
            type: 'MULTI_NUMERIC' as const,
            options: pendingComponentRef.current.options ?? [],
            required: pendingComponentRef.current.required ?? false,
          };
        }

        if (pendingComponentRef.current?.component_type === 'numeric') {
          return {
            type: 'NUMERIC' as const,
            includeFreeform: pendingComponentRef.current.include_freeform ?? false,
            required: pendingComponentRef.current.required ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'skip_only') {
          return { type: 'SKIP_ONLY' as const };
        }

        if (pendingComponentRef.current?.component_type === 'confirm_document') {
          const docType = (pendingComponentRef.current.document_type as DocumentType) || 'resume';

          return {
            type: 'CONFIRM_DOCUMENT' as const,
            documentType: docType,
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'file_upload') {
          const docType = (pendingComponentRef.current.document_type as DocumentType) || 'resume';

          return {
            type: 'FILE_UPLOAD' as const,
            documentType: docType,
            allowSkipDocument: pendingComponentRef.current.allow_skip,
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'career_paths') {
          return {
            type: 'CAREER_PATH_SELECTOR' as const,
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
            required: pendingComponentRef.current.required ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'college_types') {
          return {
            type: 'COLLEGE_TYPE_SELECTOR' as const,
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
            required: pendingComponentRef.current.required ?? true,
          };
        }

        if (pendingComponentRef.current?.component_type === 'user_suggestion') {
          return {
            type: 'USER_SUGGESTION' as const,
            users: pendingComponentRef.current.users ?? [],
            includeFreeform: pendingComponentRef.current.include_freeform ?? true,
            required: pendingComponentRef.current.required ?? false,
          };
        }

        return undefined;
      })();

      pendingComponentRef.current = null;

      if (componentType?.type === 'CAREER_PATH_SELECTOR') {
        dispatch(openCustomTab('careerPathSelector'));
      } else if (componentType?.type === 'COLLEGE_TYPE_SELECTOR') {
        dispatch(openCustomTab('collegeTypeSelector'));
      }

      const followUpButtons = done.follow_up_buttons?.length
        ? done.follow_up_buttons.map((b) => ({
            id: b.id,
            label: b.label,
            promptButtonId: b.prompt_button_id,
            prompt: b.prompt,
          }))
        : undefined;

      const aiMessage: OfficeHoursMessage = {
        text: done.response,
        sender: SENDER_TYPE.AI,
        id: String(done.response_id),
        componentType,
        followUpButtons,
        ...(done.charts.length ? { charts: done.charts } : {}),
      };

      const updatedMessages: OfficeHoursMessage[] = [...messagesToUse, ...userMessage, aiMessage];

      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(updatedMessages));
      dispatch(createSessionAction(setIsLoading, activeSession)(false));
      dispatch(officeHoursApi.util.invalidateTags(['SessionsList']));

      if (done.session_title) {
        dispatch(createSessionAction(setSessionTitle, activeSession)(done.session_title));
      }

      if (done.suggested_prompts?.length > 0) {
        dispatch(createSessionAction(setSuggestedPrompts, activeSession)(done.suggested_prompts));
      }

      if (nuggetIds.length > 0) {
        const transformedResources = transformIdsToResourcesContent(
          {
            nugget_ids: nuggetIds,
            featured_nugget_types: done.featured_nugget_types,
          },
          resourcesContentRef.current,
          officeHoursSubsessionType ?? activeSession,
        );

        handleSetResourcesContent(transformedResources);
      }

      if (done.playbook_id) {
        dispatch(openSuggestedPlaylistViewStep({ id: done.playbook_id } as PriorityPlaylist));
      }

      dispatch(createSessionAction(setArtifactStreaming, activeSession)(false));

      setStreamingContent('');
      setStreamingCharts([]);
      streamingChartsRef.current = [];
      dispatch(createSessionAction(setIsStreaming, activeSession)(false));
    };
  };

  const isRegularOfficeHours =
    activeSession === LUBAV_SUPERSESSIONS.OFFICE_HOURS && !superuser && !supercoach;

  const isSupercoachSession = activeSession === LUBAV_SUPERSESSIONS.SUPERCOACH && supercoach;

  const isMajorsSession =
    activeSession === OFFICE_HOURS_SESSION_IDS.MAJORS && !superuser && !supercoach;

  const isStructuredSession =
    STRUCTURED_SESSION_IDS.includes(activeSession as StructuredSessionId) &&
    !superuser &&
    !supercoach;

  const shouldFetchInitialMessage =
    (isRegularOfficeHours || isSupercoachSession) &&
    officeHoursMessages.length === 0 &&
    !!sessionId;

  const shouldAutoStartMajorsSession =
    isMajorsSession &&
    officeHoursMessages.length === 0 &&
    !!sessionId &&
    !loadingMostRecentUserResponseSet;

  const shouldAutoStartStructuredSession =
    isStructuredSession &&
    officeHoursMessages.length === 0 &&
    !!sessionId &&
    !loadingMostRecentUserResponseSet;

  const [getInitialMessage] = useGetInitialMessageMutation();
  const initialMessageCalledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const stoppedStreamingRef = useRef(false);

  useEffect(() => {
    if (sessionId && officeHoursMessages.length === 0) {
      initialMessageCalledRef.current = false;
      dispatch(createSessionAction(setInitialMessageFetched, activeSession)(false));
    }
  }, [sessionId, activeSession, dispatch, officeHoursMessages.length]);

  // Abort streaming when switching sessions or unmounting
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        stoppedStreamingRef.current = true;
        abortControllerRef.current.abort();
        abortControllerRef.current = null;

        keepPartialReply();

        dispatch(createSessionAction(setIsLoading, activeSession)(false));
        dispatch(createSessionAction(setIsStreaming, activeSession)(false));
        dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
      }
    };
  }, [sessionId, activeSession, dispatch, keepPartialReply]);

  useEffect(() => {
    if (shouldFetchInitialMessage && !initialMessageFetched && !initialMessageCalledRef.current) {
      initialMessageCalledRef.current = true;
      dispatch(createSessionAction(setInitialMessageFetched, activeSession)(true));

      const abortController = new AbortController();

      abortControllerRef.current = abortController;
      stoppedStreamingRef.current = false;
      dispatch(createSessionAction(setIsLoading, activeSession)(true));
      dispatch(createSessionAction(setIsStreaming, activeSession)(true));

      const handleDone = createDoneHandler('', []);

      getInitialMessage({
        session_id: sessionId ?? 0,
        signal: abortController.signal,
        onMeta: (meta) => {
          if (stoppedStreamingRef.current) return;

          handleDeltaMeta(meta);
        },
        onDelta: (accumulated) => {
          if (stoppedStreamingRef.current) return;

          handleDeltaContent(accumulated);
        },
        onTool: (tool) => {
          if (stoppedStreamingRef.current) return;

          handleDeltaTool(tool);
        },
        onChart: (chart) => {
          if (stoppedStreamingRef.current) return;

          handleChart(chart);
        },
        onComponent: (component) => {
          if (stoppedStreamingRef.current) return;

          handleDeltaComponent(component);
        },
        onDone: (done) => {
          if (stoppedStreamingRef.current) return;

          handleDone(done);
        },
      })
        .unwrap()
        .then(() => {
          abortControllerRef.current = null;
          dispatch(createSessionAction(setToolTriggered, activeSession)(false));
        })
        .catch((error) => {
          abortControllerRef.current = null;

          if (!abortController.signal.aborted) {
            console.error('Error fetching initial message:', error);
            clearStreamingReply();
            dispatch(resetStreamingState({ activeSession }));
            dispatch(
              createSessionAction(
                addOfficeHoursMessage,
                activeSession,
              )({
                text: chatBox.overloadErr,
                sender: SENDER_TYPE.AI,
              }),
            );
          }
        });
    } else if (shouldAutoStartMajorsSession && !initialMessageCalledRef.current) {
      initialMessageCalledRef.current = true;

      if (hasCompletedBlueprint) {
        sendMessage({
          message: '',
          LLMInput: '<START>',
        });
      } else {
        dispatch(
          createSessionAction(
            addOfficeHoursMessage,
            activeSession,
          )({
            text: chatBox.blueprintRequired(userFirstName),
            sender: SENDER_TYPE.AI,
          }),
        );
      }
    } else if (shouldAutoStartStructuredSession && !initialMessageCalledRef.current) {
      initialMessageCalledRef.current = true;

      sendMessage({
        message: '',
        LLMInput: '<START>',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldFetchInitialMessage,
    shouldAutoStartMajorsSession,
    shouldAutoStartStructuredSession,
    sessionId,
    getInitialMessage,
    dispatch,
    initialMessageFetched,
    hasCompletedBlueprint,
    activeSession,
    userFirstName,
  ]);

  const queuedPrompt = useAppSelector((state) => state.sessionState.queuedPrompt);

  useEffect(() => {
    if (queuedPrompt && !isTyping && !isStreaming) {
      sendMessage({
        message: queuedPrompt.chatMessage ?? queuedPrompt.label,
        LLMInput: queuedPrompt.prompt,
        promptButtonId: queuedPrompt.promptButtonId,
      });
      dispatch(clearQueuedPrompt());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedPrompt, isTyping, isStreaming]);

  const { data: userProfile } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !userId,
  });
  const enrollmentLevel = userProfile?.profile.academicEnrollmentTypes?.level;
  const isHighSchool = enrollmentLevel === 'high_school';
  const isCollegeStudent = enrollmentLevel === 'post_secondary';

  const { handleCopy, handleThumbsUp, handleThumbsDown, handleBookmark } = useMessageActions({
    activeSession,
  });

  const handleSetResourcesContent = (resourcesContent: ResourcesContent) => {
    dispatch(createSessionAction(setResourcesContent, activeSession)(resourcesContent));
  };

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null); // message being edited
  const [editingText, setEditingText] = useState(''); // text of message being edited
  const [editingComponentType, setEditingComponentType] = useState<
    OfficeHoursMessage['componentType'] | null
  >(null); // component type of message being edited (if applicable)
  const [inputValue, setInputValue] = useState(''); // input box value
  const [profileItemsToInclude, setProfileItemsToInclude] = useState<ProfileItemType[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setEditingMessageId(null);
      setEditingText('');
      setEditingComponentType(null);
      setInputValue('');
      setProfileItemsToInclude([]);
      setSelectedPromptId(null);
    }
  }, [sessionId]);

  const pendingSelectorResponse = useAppSelector((state) =>
    activeSession ? getSessionState(state, activeSession).coachLubav.pendingSelectorResponse : null,
  );

  useEffect(() => {
    if (pendingSelectorResponse && activeSession) {
      sendMessage({ message: pendingSelectorResponse });
      dispatch(createSessionAction(setPendingSelectorResponse, activeSession)(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSelectorResponse]);

  const handleEditSubmit = async (
    queryId: string,
    newText: string,
    additionalParams?: Omit<SendMessageParams, 'message' | 'currentMessageList' | 'newSessionId'>,
    createUnlinkedSession?: boolean,
  ) => {
    try {
      dispatch(createSessionAction(setIsLoading, activeSession)(true));
      setEditingMessageId(null);
      setEditingComponentType(null);
      const messageIndex = officeHoursMessages.findIndex((msg) => msg.id === queryId);

      const updatedMessages: OfficeHoursMessage[] = [
        ...officeHoursMessages.slice(0, messageIndex),
        { text: newText, sender: SENDER_TYPE.USER, id: queryId },
      ];

      // remove all subsequent messages & temporarily add the new message to the message array
      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(updatedMessages));

      const response = await getNewSessionIdForMessageEditMutation({
        session_id: sessionId ?? 0,
        query_id: queryId,
        create_unlinked_session: createUnlinkedSession,
      }).unwrap();

      const newSessionId = response.session_id;

      dispatch(createSessionAction(setSessionId, activeSession)(newSessionId));

      // remove the temporary message, will be replaced by the message with an id in sendMessage
      const messagesWithTempMessageRemoved = updatedMessages.slice(0, messageIndex);

      dispatch(
        createSessionAction(setOfficeHoursMessages, activeSession)(messagesWithTempMessageRemoved),
      );

      await sendMessage({
        message: newText,
        currentMessageList: messagesWithTempMessageRemoved,
        newSessionId,
        ...additionalParams,
      });

      dispatch(createSessionAction(setIsLoading, activeSession)(false));
    } catch (error) {
      console.error('Error editing message:', error);
      dispatch(createSessionAction(setIsLoading, activeSession)(false));
    }
  };

  /**
   * Sends a message to Lubav
   * `message` is the message as it appears in the chat box
   * `LLMInput` is the message as received by the LLM
   */
  async function sendMessage({
    message,
    LLMInput,
    currentMessageList,
    newSessionId,
    requiresDocument,
    confirmedDocuments,
    allowSkipDocument,
    includeProfileItems,
    officeHoursSubsessionType: passedSubsessionType,
    attachedNuggets,
    attachedFiles: passedAttachedFiles,
    promptButtonId,
  }: SendMessageParams) {
    if (!activeSession) {
      return null;
    }

    const hasFiles = passedAttachedFiles && passedAttachedFiles.length > 0;

    if ((!message.trim() && !LLMInput) || !isUserOrGuest) return;

    // Talking to Lubav instead of answering the prompt drops the previewed session, and with it
    // the tab that was selected to show it.
    if (previewedSession) {
      dispatch(setPreviewedSession(null));
      dispatch(setSelectedTabKey(null));
    }

    const allProfileItemsToInclude = Array.from(
      new Set([...profileItemsToInclude, ...(includeProfileItems || [])]),
    );

    const messagesToUse = currentMessageList || officeHoursMessages;
    const handleDone = createDoneHandler(
      message,
      messagesToUse,
      attachedNuggets,
      passedAttachedFiles,
    );

    // extract type/id for API, keep full data for display
    const nuggetRefs = attachedNuggets?.map((n) => ({ type: n.type, id: n.id, title: n.title }));

    if (message?.trim() || hasFiles) {
      const componentType = hasFiles
        ? { type: 'ATTACHED_FILES' as const, attachments: passedAttachedFiles }
        : attachedNuggets?.length
          ? { type: 'ATTACHED_NUGGETS' as const, nuggets: attachedNuggets }
          : undefined;

      const tempUserMessage: OfficeHoursMessage = {
        text: message || '',
        sender: SENDER_TYPE.USER,
        componentType,
      };

      dispatch(createSessionAction(addOfficeHoursMessage, activeSession)(tempUserMessage));
    }

    // Track pending document requirements for re-sending after upload/confirmation
    if (requiresDocument?.length) {
      pendingDocReqRef.current = {
        llmInput: LLMInput || message,
        promptButtonId,
        requiredDocuments: requiresDocument,
        allowSkipDocument: allowSkipDocument ?? false,
        confirmedDocuments: confirmedDocuments || [],
      };
    } else {
      pendingDocReqRef.current = null;
    }

    const sessionIdToUse = newSessionId || sessionId;

    if (!sessionIdToUse) {
      return;
    }

    // Use window.history directly (not navigate) so useSearchParams consumers
    // don't re-render mid-send and re-fire the session-recall query.
    if (loggedIn) {
      const urlParams = new URLSearchParams(window.location.search);
      const nextSessionId = String(sessionIdToUse);

      if (urlParams.get('session_id') !== nextSessionId) {
        urlParams.set('session_id', nextSessionId);
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`,
        );
      }
    }

    dispatch(createSessionAction(resetSuggestedPrompts, activeSession)(undefined));
    dispatch(createSessionAction(setIsLoading, activeSession)(true));
    dispatch(createSessionAction(setIsStreaming, activeSession)(true));

    const abortController = new AbortController();

    abortControllerRef.current = abortController;
    featuredTypesRef.current = [];

    try {
      const sessionIdToUse = newSessionId || sessionId;

      if (!sessionIdToUse) {
        return;
      }

      await sendMessageMutation({
        text: LLMInput ? LLMInput : message,
        display_text: LLMInput ? message : undefined,
        session_id: sessionIdToUse,
        user_id: userId,
        llm: LLM,
        language: language,
        is_guidance_counselor: isGuidanceCounselor,
        as_superuser: superuser,
        as_supercoach: supercoach,
        coaching_user_id: coachingUser?.id ? Number(coachingUser.id) : undefined,
        include_profile_items:
          allProfileItemsToInclude.length > 0 ? allProfileItemsToInclude : undefined,
        ...(superuser || supercoach
          ? {}
          : {
              office_hours_session_type:
                passedSubsessionType || officeHoursSubsessionType || activeSession,
            }),
        attached_nuggets: nuggetRefs,
        attached_files: passedAttachedFiles,
        prompt_button_id: promptButtonId,
        agent_context: agentCtx?.getContext(),
        required_documents: requiresDocument,
        allow_skip_document: allowSkipDocument,
        confirmed_documents: confirmedDocuments,
        signal: abortController.signal,
        onMeta: handleDeltaMeta,
        onDelta: handleDeltaContent,
        onArtifact: handleArtifact,
        onChart: handleChart,
        onTool: handleDeltaTool,
        onNugget: handleNugget,
        onFeaturedType: handleFeaturedType,
        onComponent: handleDeltaComponent,
        onDone: handleDone,
      }).unwrap();

      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      dispatch(createSessionAction(setToolTriggered, activeSession)(false));
    } catch (error) {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      // The stream was aborted intentionally; whoever aborted it owns the
      // streaming state, so don't reset it here.
      if (abortController.signal.aborted) {
        return;
      }

      console.error(chatBox.fetchErr, error);

      clearStreamingReply();
      await dispatch(resetStreamingState({ activeSession })).unwrap();

      pendingDocReqRef.current = null;
      setSelectedPromptId(null);
      // for now we assume an overload error if we get any error
      // TODO: handle errors on a case by case basis
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          activeSession,
        )({
          text: chatBox.overloadErr,
          sender: SENDER_TYPE.AI,
        }),
      );
    }
  }

  const handleEdit = async (queryId: string) => {
    try {
      if (editingMessageId === queryId) {
        setEditingMessageId(null);
        setEditingText('');
        setEditingComponentType(null);

        return;
      }

      const messageIndex = officeHoursMessages.findIndex((msg) => msg.id === queryId);
      const message = officeHoursMessages[messageIndex];

      if (!message) {
        return;
      }

      const precedingMessage = messageIndex > 0 ? officeHoursMessages[messageIndex - 1] : null;
      const precedingComponentType =
        precedingMessage?.sender === SENDER_TYPE.AI ? precedingMessage.componentType : null;

      setEditingMessageId(queryId);
      setEditingText(message.text || '');
      setEditingComponentType(precedingComponentType ?? null);
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const shouldShowUserSelect = (message: OfficeHoursMessage, index: number): boolean => {
    return (
      message.sender === SENDER_TYPE.AI &&
      superuser &&
      (message.nuggets?.users?.length || 0) > 0 &&
      index === officeHoursMessages.length - 1
    );
  };

  const handleFileUploadSubmit = (
    fileNames: string[],
    documentType: DocumentType,
    content?: string,
    attachedFiles?: AttachedFilePayload[],
  ) => {
    const pending = pendingDocReqRef.current;
    // Show the upload as a document card; the filename text is only needed when there's
    // no LLM input to carry the send (a message needs text or input to be sent).
    const fileDisplay = chatBox.fileUpload.fileNameDisplay(fileNames);

    if (pending) {
      const newConfirmedDocs = [...pending.confirmedDocuments, documentType];
      const llmInput = pending.promptButtonId ? undefined : pending.llmInput;

      pendingDocReqRef.current = { ...pending, confirmedDocuments: newConfirmedDocs };
      sendMessage({
        message: attachedFiles?.length && llmInput ? '' : fileDisplay,
        LLMInput: llmInput,
        promptButtonId: pending.promptButtonId,
        requiresDocument: pending.requiredDocuments,
        confirmedDocuments: newConfirmedDocs,
        allowSkipDocument: pending.allowSkipDocument,
        attachedFiles,
      });
    } else {
      sendMessage({
        message: attachedFiles?.length && content ? '' : fileDisplay,
        LLMInput: content,
        attachedFiles,
      });
    }
  };

  const handleFileUploadSkip = (documentType: DocumentType) => {
    const pending = pendingDocReqRef.current;

    if (pending) {
      const newConfirmedDocs = [...pending.confirmedDocuments, documentType];

      pendingDocReqRef.current = { ...pending, confirmedDocuments: newConfirmedDocs };
      sendMessage({
        message: chatBox.fileUpload.skipDocument,
        LLMInput: pending.promptButtonId ? undefined : pending.llmInput,
        promptButtonId: pending.promptButtonId,
        requiresDocument: pending.requiredDocuments,
        confirmedDocuments: newConfirmedDocs,
        allowSkipDocument: pending.allowSkipDocument,
      });
    }
  };

  const renderSkipButton = (documentType: DocumentType) => (
    <Button
      id="chat-box-skip-document-button"
      onClick={() => handleFileUploadSkip(documentType)}
      sx={skipButtonStyle}
    >
      <Typography variant="text-xs-semibold">{chatBox.fileUpload.skipDocument}</Typography>
    </Button>
  );

  const handleFileUploadError = (message?: string) => {
    dispatch(
      createSessionAction(
        updateLastMessage,
        activeSession,
      )({
        componentType: undefined,
      }),
    );
    dispatch(
      createSessionAction(
        addOfficeHoursMessage,
        activeSession,
      )({
        text: message ?? chatBox.fileUpload.uploadError,
        sender: SENDER_TYPE.AI,
        componentType: { type: 'FILE_UPLOAD', documentType: 'resume' },
      }),
    );
  };

  const handleInputSend = () => {
    if (!sessionId) return;
    if (isTyping || isStreaming) return;

    const doneFiles = attachedFiles.filter((f) => f.uploadStatus === 'done');
    const filePayloads: AttachedFilePayload[] | undefined =
      doneFiles.length > 0
        ? doneFiles.map((f) => ({
            s3_key: f.s3_key,
            filename: f.filename,
            mime_type: f.mime_type,
          }))
        : undefined;

    sendMessage({
      message: inputValue,
      newSessionId: sessionId,
      attachedNuggets: attachedNuggets.length > 0 ? [...attachedNuggets] : undefined,
      attachedFiles: filePayloads,
    });
    setInputValue('');
    clearNuggets();
    clearFiles();
  };

  const handleNuggetDrop = useCallback(
    (nugget: AttachedNugget) => {
      addNugget({
        id: nugget.id,
        type: nugget.type,
        title: nugget.title,
      });
    },
    [addNugget],
  );

  const handleStopStreaming = () => {
    stoppedStreamingRef.current = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    keepPartialReply();

    dispatch(createSessionAction(setIsLoading, activeSession)(false));
    dispatch(createSessionAction(setIsStreaming, activeSession)(false));
    dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
  };

  const handleSuggestedPromptSelect = (params: SendMessageParams) => {
    if (params.includeProfileItems?.length) {
      setProfileItemsToInclude((prev) =>
        Array.from(new Set([...prev, ...(params.includeProfileItems || [])])),
      );
    }

    sendMessage(params);
  };

  const handleNuggetReferenceClick = (nuggetType: string, nuggetId: string) => {
    const resourceKey = nuggetTypeToResourceKey[nuggetType];

    if (!resourceKey || !activeSession) return;

    const resourceContent = resourcesContent?.[resourceKey as keyof typeof resourcesContent];
    const nuggetItems = resourceContent?.content?.content as
      | { id: string; slug?: string; tab?: BoxType }[]
      | undefined;
    const nuggetItem = nuggetItems?.find(
      (item) => String(item.id) === nuggetId || item.slug === nuggetId,
    );
    const tab = nuggetItem?.tab ?? resourceKeyToTab[resourceKey];

    if (nuggetItem && tab) {
      dispatch(
        createSessionAction(
          setHighlightedNugget,
          activeSession,
        )({ resourceKey, id: String(nuggetItem.id), tab }),
      );
      dispatch(setSelectedTabKey(`resource:${tab}`));

      if (isMobile) {
        dispatch(closeMobileFab());
      }
    }
  };

  const findNuggetItem = (nuggetType: string, nuggetId: string): ContentNugget | undefined => {
    const resourceKey = nuggetTypeToResourceKey[nuggetType];

    if (!resourceKey) return undefined;

    const items = resourcesContent?.[resourceKey as keyof typeof resourcesContent]?.content
      ?.content as ContentNugget[] | undefined;

    return items?.find((item) => String(item.id) === nuggetId || item.slug === nuggetId);
  };

  const isNuggetAvailable = (nuggetType: string, nuggetId: string): boolean =>
    !!findNuggetItem(nuggetType, nuggetId);

  const getNuggetHref = (nuggetType: string, nuggetId: string): string | undefined => {
    const nugget = findNuggetItem(nuggetType, nuggetId);

    return nugget ? getShareUrl({ contentNugget: nugget }) : undefined;
  };

  const handleDocumentClick = (documentType: string) => {
    const docType = documentType as DocumentType;
    const document = documents?.[docType]?.[0];

    if (document?.url) {
      window.open(document.url, '_blank');
    }
  };

  // #signup links are only emitted by Lubav for guest visitors
  const handleSignupClick = isGuest ? () => dispatch(openSignupPrompt()) : undefined;

  const buildPromptSendParams = (
    option: PromptOption,
    subsessionType?: LubavOfficeHoursSubsessionType,
  ) => ({
    LLMInput: option.promptButtonId ? undefined : option.hiddenPrompt || option.title,
    promptButtonId: option.promptButtonId,
    officeHoursSubsessionType: subsessionType,
    requiresDocument: option.requireDocument,
    allowSkipDocument: option.allowSkipDocument,
    includeProfileItems: option.includeProfileItems,
  });

  // Enter a subsession in its own fresh session. Uses initiateSession, not the edit
  // branch below — switching the session id mid-send trips the abort-on-session-switch effect.
  const startSubsession = async (option: PromptOption) => {
    if (!activeSession) return;

    const subsessionType = option.sessionId as LubavOfficeHoursSubsessionType;
    const params = buildPromptSendParams(option, subsessionType);

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    const hasPriorConversation = officeHoursMessages.some((m) => m.sender === SENDER_TYPE.USER);

    // No prior conversation: the greeting is already in place, so just send into this session.
    if (!hasPriorConversation) {
      sendMessage({ message: option.title, ...params });

      return;
    }

    // Carry the greeting into the fresh session; a non-empty message list also stops the refetch.
    const greeting = officeHoursMessages[0];

    // Show the user's turn + thinking right away so the buttons don't blank out during creation.
    dispatch(
      createSessionAction(
        setOfficeHoursMessages,
        activeSession,
      )([greeting, { text: option.title, sender: SENDER_TYPE.USER }]),
    );
    dispatch(createSessionAction(setIsLoading, activeSession)(true));
    dispatch(createSessionAction(resetBoxData, activeSession)(undefined));

    try {
      const session = await dispatch(
        initiateSession({ path: '', officeHoursId: activeSession, activeSession }),
      ).unwrap();

      if (!session?.sessionId) {
        dispatch(resetStreamingState({ activeSession }));
        dispatch(
          createSessionAction(
            addOfficeHoursMessage,
            activeSession,
          )({ text: chatBox.overloadErr, sender: SENDER_TYPE.AI }),
        );

        return;
      }

      // initiateSession clears loading on success — re-hold it so the indicator stays continuous.
      dispatch(createSessionAction(setIsLoading, activeSession)(true));

      // Persist the greeting into the new session so it survives a reload (guest sessions become
      // recallable once claimed on signup). Best-effort — don't block the subsession on it.
      if (greeting.text) {
        try {
          await appendMessageMutation({
            session_id: session.sessionId,
            role: greeting.sender === SENDER_TYPE.AI ? 'ai' : 'user',
            text: greeting.text,
          }).unwrap();
        } catch (error) {
          console.warn('Could not carry the greeting into the new subsession', error);
        }
      }

      // Drop the optimistic turn; sendMessage re-adds it with its real id in the same tick.
      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)([greeting]));

      await sendMessage({
        message: option.title,
        ...params,
        currentMessageList: [greeting],
        newSessionId: session.sessionId,
      });
    } catch (error) {
      console.error('Failed to start subsession', error);
      dispatch(resetStreamingState({ activeSession }));
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          activeSession,
        )({ text: chatBox.overloadErr, sender: SENDER_TYPE.AI }),
      );
    }
  };

  const handlePromptButtonClick = (option: PromptOption) => {
    setSelectedPromptId(option.id);

    if (isOfficeHoursSubsessionType(option.sessionId)) {
      void startSubsession(option);

      return;
    }

    const sendMessageParams = buildPromptSendParams(option);

    // if there's more than one AI message, start a new session via edit
    const aiMessagesCount = officeHoursMessages.filter((m) => m.sender === SENDER_TYPE.AI).length;

    if (aiMessagesCount > 1) {
      const firstUserMessage = officeHoursMessages.find((m) => m.sender === SENDER_TYPE.USER);

      if (firstUserMessage?.id) {
        dispatch(createSessionAction(resetBoxData, activeSession)(undefined));
        handleEditSubmit(firstUserMessage.id, option.title, sendMessageParams, true);

        return;
      }
    }

    sendMessage({
      message: option.title,
      ...sendMessageParams,
    });
  };

  useLubavLaunchParams({
    activeSession,
    sessionId,
    isReady: !isTyping && !isStreaming && officeHoursMessages.length > 0,
    isGuest,
    isHighSchool,
    onTopic: handlePromptButtonClick,
    onQuery: (message) => sendMessage({ message }),
  });

  const handlePagePromptClick = (option: PromptOption) => {
    const prompt = pagePrompts.find((p: PagePrompt) => p.promptButtonId === option.id);

    if (!prompt) return;

    sendMessage({
      message: prompt.title,
      promptButtonId: prompt.promptButtonId,
    });
  };

  const handleFollowUpClick = (option: PromptOption) => {
    if (option.sessionSwitch) {
      // A structured-session switch prompt is transient: pass no fromSession so the choice
      // isn't echoed into the session or persisted.
      const isTransientSwitchPrompt = pendingStructuredChoice === activeSession;

      dispatch(
        applySessionSwitch({
          choice: option.sessionSwitch,
          fromSession: isTransientSwitchPrompt ? null : activeSession,
          fromSessionId: isTransientSwitchPrompt ? undefined : sessionId,
          choiceLabel: option.title,
        }),
      );

      return;
    }

    if (!option.hiddenPrompt && !option.promptButtonId) return;

    const docRequirements = option.promptButtonId
      ? COACHING_LEAF_DOC_REQUIREMENTS[option.promptButtonId]
      : undefined;

    sendMessage({
      message: option.title,
      LLMInput: option.hiddenPrompt,
      promptButtonId: option.promptButtonId,
      requiresDocument: docRequirements?.requireDocument,
      allowSkipDocument: docRequirements?.allowSkipDocument,
      includeProfileItems: docRequirements?.includeProfileItems,
    });
  };

  const scrollKey = useMemo(() => {
    const messageCount = officeHoursMessages.length;
    const suggestedPromptsCount = suggestedPrompts?.length || 0;

    return `${messageCount}-${suggestedPromptsCount}`;
  }, [officeHoursMessages, suggestedPrompts]);

  const pagePromptOptions: PromptOption[] = useMemo(
    () =>
      pagePrompts.map((p: PagePrompt) => ({
        id: p.promptButtonId,
        title: p.title,
        promptButtonId: p.promptButtonId,
      })),
    [pagePrompts],
  );

  if (!activeSession || !isShellManagedSession(activeSession) || (!isUserOrGuest && !isShared)) {
    return null;
  }

  // Show a skeleton only while loading an existing session's saved messages.
  const showChatSkeleton = loadingSessionData === activeSession && officeHoursMessages.length === 0;

  const renderMessages = () => (
    <>
      {superuser && (
        <UserSearch
          onClick={(message, LLMInput) => sendMessage({ message, LLMInput })}
          asSuperuser={true}
          userId={userId}
          sessionId={sessionId}
        />
      )}
      {showChatSkeleton && <ChatSkeleton />}
      {officeHoursMessages.map((msg, index) => {
        if (!msg.text?.trim() && !msg.componentType && !msg.charts?.length) {
          return null;
        }

        const renderInputComponent = (
          componentType: OfficeHoursMessage['componentType'],
          handlers: {
            onSelectOption: (option: string, additionalParams?: Partial<SendMessageParams>) => void;
            onFileUpload: (
              fileNames: string[],
              documentType: DocumentType,
              content?: string,
              attachedFiles?: AttachedFilePayload[],
            ) => void;
            onSwitchToFileUpload?: (documentType: DocumentType) => void;
          },
          options?: { allowSkipDocument?: boolean; isEditing?: boolean },
        ) => {
          if (!componentType) return null;

          if (isSharedTranscript || !isUserOrGuest) return null;

          if (componentType.type === 'SELECTED_USER' && componentType.user) {
            return <SelectedUser user={componentType.user} />;
          }

          // Structured-session question types (single_select, multi_select, multi_numeric,
          // numeric, skip_only, user_suggestion). Only render on the last message unless editing.
          const isStructuredQuestion =
            componentType.type === 'SINGLE_SELECT' ||
            componentType.type === 'MULTI_SELECT' ||
            componentType.type === 'MULTI_NUMERIC' ||
            componentType.type === 'SKIP_ONLY' ||
            componentType.type === 'NUMERIC' ||
            componentType.type === 'USER_SUGGESTION';

          if (isStructuredQuestion) {
            if (!options?.isEditing && index !== officeHoursMessages.length - 1) {
              return null;
            }

            return (
              <StructuredQuestionInput
                componentType={componentType}
                messageId={msg.id ?? ''}
                isTyping={isTyping}
                onSelectOption={handlers.onSelectOption}
              />
            );
          }

          if (componentType.type === 'CAREER_PATH_SELECTOR') {
            // On desktop, render in InfoBox instead
            if (!isMobile) return null;

            // Only show on last message unless editing
            if (!options?.isEditing && index !== officeHoursMessages.length - 1) {
              return null;
            }

            const skipHandler =
              componentType.required === false ? () => handlers.onSelectOption('None') : undefined;

            return (
              <CareerPathSelector
                onSubmit={(formattedSelection) => handlers.onSelectOption(formattedSelection)}
                onSkip={skipHandler}
              />
            );
          }

          if (componentType.type === 'COLLEGE_TYPE_SELECTOR') {
            // On desktop, render in InfoBox instead
            if (!isMobile) return null;

            // Only show on last message unless editing
            if (!options?.isEditing && index !== officeHoursMessages.length - 1) {
              return null;
            }

            const skipHandler =
              componentType.required === false ? () => handlers.onSelectOption('None') : undefined;

            return (
              <CollegeTypeSelector
                onSubmit={(selectedTypes) => handlers.onSelectOption(selectedTypes.join(', '))}
                onSkip={skipHandler}
              />
            );
          }

          if (componentType.type === 'FILE_UPLOAD') {
            const documentType = componentType.documentType;

            if (!documentType) return null;
            if (!userId) return null;

            // A generic request (a field name like 'document_upload', not a real type)
            // is left undefined so the upload is auto-classified.
            const requestedDocumentType = (Object.values(DOCUMENT_TYPES) as string[]).includes(
              documentType,
            )
              ? documentType
              : undefined;

            // Only show if this is the last message (user hasn't responded yet)
            if (!options?.isEditing) {
              const isLastMessage = index === officeHoursMessages.length - 1;

              if (!isLastMessage) return null;
            }

            return (
              <FileUpload
                userId={coachingUser?.id ? parseInt(coachingUser.id) : userId}
                addToProfile={true}
                documentType={requestedDocumentType}
                onSubmit={(
                  fileNames: string[],
                  content?: string,
                  attachedFiles?: AttachedFilePayload[],
                ) => handlers.onFileUpload(fileNames, documentType, content, attachedFiles)}
                onError={handleFileUploadError}
                cancelComponent={options?.allowSkipDocument ? renderSkipButton(documentType) : null}
              />
            );
          }

          if (componentType.type === 'CONFIRM_DOCUMENT') {
            const documentType = componentType.documentType as DocumentType;

            // Only show if this is the last message (user hasn't responded yet)
            if (!options?.isEditing) {
              const isLastMessage = index === officeHoursMessages.length - 1;

              if (!isLastMessage) return null;
            }

            const uploadNewOption = chatBox.fileUpload.uploadANewDocument(
              DOCUMENT_NAMES[documentType],
              getCoachingUserName(coachingUser),
            );
            const useExistingOption = chatBox.fileUpload.iWantToUseMyCurrentDocument(
              DOCUMENT_NAMES[documentType],
              getCoachingUserName(coachingUser),
            );

            const confirmDocumentOptions: PromptOption[] = [
              { id: 'upload_new', title: uploadNewOption },
              { id: 'use_existing', title: useExistingOption },
            ];

            return (
              <PromptButtons
                onClick={(option) => {
                  if (option.id === 'upload_new') {
                    // Switch to file upload mode
                    if (handlers.onSwitchToFileUpload) {
                      handlers.onSwitchToFileUpload(documentType);
                    } else {
                      // Default behavior: add a new message with FILE_UPLOAD component
                      dispatch(
                        createSessionAction(
                          updateLastMessage,
                          activeSession,
                        )({
                          componentType: undefined,
                        }),
                      );
                      dispatch(
                        createSessionAction(
                          addOfficeHoursMessage,
                          activeSession,
                        )({
                          text: chatBox.fileUpload.uploadUpdatedDocument(
                            DOCUMENT_NAMES[documentType],
                            getCoachingUserName(coachingUser),
                          ),
                          sender: SENDER_TYPE.AI,
                          componentType: { type: 'FILE_UPLOAD', documentType },
                        }),
                      );
                    }
                  } else {
                    // Confirm using existing document
                    if (!handlers.onSwitchToFileUpload) {
                      dispatch(
                        createSessionAction(
                          updateLastMessage,
                          activeSession,
                        )({
                          componentType: undefined,
                        }),
                      );
                    }

                    const pending = pendingDocReqRef.current;
                    const newConfirmedDocs = pending
                      ? [...pending.confirmedDocuments, documentType]
                      : [documentType];

                    if (pending) {
                      pendingDocReqRef.current = {
                        ...pending,
                        confirmedDocuments: newConfirmedDocs,
                      };
                    }

                    handlers.onSelectOption(useExistingOption, {
                      LLMInput: pending?.promptButtonId
                        ? undefined
                        : pending?.llmInput || useExistingOption,
                      promptButtonId: pending?.promptButtonId,
                      requiresDocument: pending?.requiredDocuments || undefined,
                      confirmedDocuments: newConfirmedDocs,
                      allowSkipDocument: pending?.allowSkipDocument,
                    });
                  }
                }}
                isTyping={isTyping}
                promptOptions={confirmDocumentOptions}
                variant="SINGLE_SELECT"
              />
            );
          }

          if (componentType.type === 'ATTACHED_NUGGETS' && componentType.nuggets.length > 0) {
            return (
              <Box sx={attachedNuggetsContainerSx}>
                {componentType.nuggets.map((nugget) => (
                  <Box key={`${nugget.type}-${nugget.id}`}>
                    <AttachedNuggetChip id={nugget.id} type={nugget.type as ResourceKey} />
                  </Box>
                ))}
              </Box>
            );
          }

          if (componentType.type === 'ATTACHED_FILES' && componentType.attachments.length > 0) {
            return (
              <Box sx={attachedFilesMessageSx}>
                {componentType.attachments.map((file, i) => {
                  const { source_url: sourceUrl, s3_key: s3Key } = file;
                  // Message index rather than msg.id: an optimistic message has no id yet,
                  // so the id would change under a test as soon as the send landed.
                  const slot = `${index}-${i}`;

                  if (sourceUrl) {
                    return (
                      <Box
                        key={`attached-link-${slot}`}
                        id={`chat-attached-link-${slot}`}
                        sx={attachedLinkSx}
                        onClick={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
                      >
                        <UploadedFile fileName={file.filename} fileSize="" fileType="link" />
                      </Box>
                    );
                  }

                  const card = (
                    <UploadedFile
                      key={`attached-file-${slot}`}
                      fileName={file.filename}
                      fileSize=""
                      fileType={getFileExtensionFromMimeType(file.mime_type)}
                    />
                  );

                  if (!s3Key) {
                    return card;
                  }

                  return (
                    <Box
                      key={`attached-file-${slot}`}
                      id={`chat-attached-file-${slot}`}
                      sx={attachedLinkSx}
                      onClick={() => handleOpenAttachment(s3Key)}
                    >
                      {card}
                    </Box>
                  );
                })}
              </Box>
            );
          }

          return null;
        };

        const sendMessageHandlers = {
          onSelectOption: (option: string, additionalParams?: Partial<SendMessageParams>) => {
            sendMessage({ message: option, ...additionalParams });
          },
          onFileUpload: (
            fileNames: string[],
            documentType: DocumentType,
            content?: string,
            attachedFiles?: AttachedFilePayload[],
          ) => {
            handleFileUploadSubmit(fileNames, documentType, content, attachedFiles);
          },
        };

        // if the message has a component type, map to it and display it
        const getCustomComponentToDisplay = (msg: OfficeHoursMessage) => {
          return renderInputComponent(msg.componentType, sendMessageHandlers, {
            allowSkipDocument:
              msg.componentType?.type === 'FILE_UPLOAD' && msg.componentType.allowSkipDocument,
          });
        };

        const promptOptions = getOfficeHoursPromptOptions(activeSession, isHighSchool, isGuest);
        const shouldShowPromptsInMessage = getShouldShowPromptsInMessage({
          activeSession,
          currentMessage: msg,
          currentMessageIndex: index,
          officeHoursMessages,
          isTyping,
          isStreaming,
        });

        const isLastAiMessage =
          msg.sender === SENDER_TYPE.AI &&
          index === officeHoursMessages.length - 1 &&
          !isStreaming &&
          !isTyping;

        const messageComponent = msg.componentType ? (
          getCustomComponentToDisplay(msg)
        ) : msg.followUpButtons && msg.followUpButtons.length > 0 ? (
          <PromptButtons
            onClick={handleFollowUpClick}
            isTyping={isStreaming}
            promptOptions={msg.followUpButtons.map((b) => ({
              id: b.id,
              title: b.label,
              hiddenPrompt: b.prompt,
              promptButtonId: b.promptButtonId,
              sessionSwitch: b.sessionSwitch,
            }))}
            variant="SINGLE_SELECT"
          />
        ) : isLastAiMessage && pagePromptOptions.length > 0 ? (
          <PromptButtons
            onClick={handlePagePromptClick}
            isTyping={isTyping}
            promptOptions={pagePromptOptions}
            variant="SINGLE_SELECT"
            disabled={isStreaming}
          />
        ) : shouldShowPromptsInMessage &&
          promptOptions &&
          (isHighSchool || isCollegeStudent || isGuest || supercoach) &&
          pagePrompts.length === 0 ? (
          <PromptButtons
            onClick={handlePromptButtonClick}
            isTyping={isTyping}
            promptOptions={promptOptions}
            variant={getPromptVariant(activeSession)}
            selectedOptionId={selectedPromptId}
          />
        ) : null;

        const chartsComponent = msg.charts?.length ? <MessageCharts charts={msg.charts} /> : null;

        // Determine if this message is being edited and what type of edit
        const isEditingThisMessage = msg.sender === SENDER_TYPE.USER && editingMessageId === msg.id;
        const isTextEditing = isEditingThisMessage && !editingComponentType;
        const isComponentEditing = isEditingThisMessage && editingComponentType;

        return (
          <Box key={index} className="message-wrapper">
            <Message
              text={msg.text || ''}
              contentRef={(el) => {
                messageContentRefs.current.set(index, el);
              }}
              sender={msg.sender}
              leading={chartsComponent}
              component={messageComponent}
              componentInsideMessage={msg.componentInsideMessage}
              actionButtons={
                !isShared && msg.sender === SENDER_TYPE.AI && !msg.componentType ? (
                  <AIMessageActionsButton
                    onCopy={() => handleCopy(msg.text || '', messageContentRefs.current.get(index))}
                    onThumbsUp={() => handleThumbsUp(msg.id || '')}
                    onThumbsDown={() => handleThumbsDown(msg.id || '')}
                    onBookmark={() => handleBookmark(msg.text || '')}
                  />
                ) : undefined
              }
              onEdit={isShared ? undefined : () => handleEdit(msg.id || '')}
              isEditing={isTextEditing}
              editingText={editingText}
              onEditingTextChange={setEditingText}
              onEditSubmit={() => {
                if (editingText.trim()) {
                  handleEditSubmit(msg.id || '', editingText);
                }
              }}
              onEditCancel={() => {
                setEditingMessageId(null);
                setEditingText('');
                setEditingComponentType(null);
              }}
              onNuggetReferenceClick={handleNuggetReferenceClick}
              isNuggetAvailable={isNuggetAvailable}
              getNuggetHref={getNuggetHref}
              onDocumentClick={handleDocumentClick}
              onSignupClick={handleSignupClick}
            />

            {/* Render edit components for SINGLE_SELECT, FILE_UPLOAD, or CONFIRM_DOCUMENT */}
            {isComponentEditing &&
              renderInputComponent(
                editingComponentType,
                {
                  onSelectOption: (option, additionalParams) => {
                    handleEditSubmit(msg.id || '', option, additionalParams);
                  },
                  onFileUpload: (fileNames, documentType, content) => {
                    handleEditSubmit(msg.id || '', chatBox.fileUpload.fileNameDisplay(fileNames), {
                      LLMInput: content,
                      confirmedDocuments: [documentType],
                    });
                  },
                  onSwitchToFileUpload: (documentType) => {
                    setEditingComponentType({ type: 'FILE_UPLOAD', documentType });
                  },
                },
                { isEditing: true },
              )}

            {!msg.componentType && shouldShowUserSelect(msg, index) && (
              <Box sx={messageActionsContainer}>
                <UserSelect
                  contacts={(msg?.nuggets?.users || []).map((contact) => ({
                    id: contact.id,
                    relevance: contact.relevance,
                    name: contact.metadata?.name || '',
                    role: contact.metadata?.role === 'professional' ? 'professional' : 'student',
                    jobTitle: contact.metadata?.job_title,
                    employer: contact.metadata?.employer,
                  }))}
                  onClick={(message, LLMInput) => sendMessage({ message, LLMInput })}
                  sessionId={sessionId}
                />
              </Box>
            )}
          </Box>
        );
      })}
      {isStreaming && (streamingContent || streamingCharts.length > 0) && (
        <Box className="message-wrapper">
          <Message
            text={streamingContent}
            sender={SENDER_TYPE.AI}
            leading={streamingCharts.length > 0 ? <MessageCharts charts={streamingCharts} /> : null}
            component={null}
            onSignupClick={handleSignupClick}
          />
        </Box>
      )}
    </>
  );

  const renderSuggestedPrompts = () => {
    if (!isTyping) {
      return <SuggestedPrompts prompts={suggestedPrompts} onSelect={handleSuggestedPromptSelect} />;
    }

    return null;
  };

  const isInputDisabled = () => {
    const lastMsg = officeHoursMessages[officeHoursMessages.length - 1];

    if (
      lastMsg?.componentType?.type === 'FILE_UPLOAD' ||
      lastMsg?.componentType?.type === 'CONFIRM_DOCUMENT'
    ) {
      return true;
    }

    // for the majors session, users must have completed a Blueprint
    if (isMajorsSession && !hasCompletedBlueprint) {
      return true;
    }

    const lastMessage = officeHoursMessages[officeHoursMessages.length - 1];

    if (lastMessage?.componentType?.type === 'NUMERIC') {
      return false;
    }

    if (lastMessage?.componentType && 'includeFreeform' in lastMessage.componentType) {
      if (lastMessage.componentType.includeFreeform === false) {
        return true;
      }
    }

    return false;
  };

  const isNumericInput =
    officeHoursMessages[officeHoursMessages.length - 1]?.componentType?.type === 'NUMERIC';

  return (
    <ChatBoxContainer
      isTyping={isTyping && !streamingContent && !showChatSkeleton}
      inputProps={{
        value: inputValue,
        onChange: setInputValue,
        onSend: handleInputSend,
        onStop: handleStopStreaming,
        isNumeric: isNumericInput,
        disabled: isInputDisabled(),
        sendButtonDisabled: isTyping || isStreaming,
        isStreaming,
        placeholder: appStrings.modules.inputBox.askLubav,
        attachedNuggets,
        onNuggetDrop: handleNuggetDrop,
        onNuggetRemove: removeNugget,
        renderAttachedNugget: (nugget, onRemove) => (
          <AttachedNuggetChip
            id={nugget.id}
            type={nugget.type as ResourceKey}
            onRemove={onRemove}
          />
        ),
        attachedFiles,
        onFileSelect: isGuest ? undefined : handleFileSelect, // hide file upload for guest users
        onFileRemove: handleFileRemove,
        onFileValidationError: handleFileValidationError,
      }}
      suggestedPrompts={renderSuggestedPrompts()}
      showSuggestedPrompts={!isTyping || supercoach}
      stickySuggestedPrompts={true}
      scrollKey={scrollKey}
      headerButtons={null}
      activeTools={activeTools}
      readOnly={isSharedTranscript || !isUserOrGuest}
    >
      {renderMessages()}
      <Snackbar
        open={fileError !== null}
        autoHideDuration={4000}
        onClose={() => setFileError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={fileError ?? ''}
      />
    </ChatBoxContainer>
  );
};

const messageActionsContainer: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  marginBottom: backrs.spacing['spacing-md'],
});

const skipButtonStyle: SxProps<Theme> = ({ backrs }) => ({
  width: 'fit-content',
  textTransform: 'none',
  color: backrs.colors['Gray/700'],
  '&:hover': {
    backgroundColor: 'transparent',
    '& .MuiTypography-root': {
      color: backrs.colors['Gray/400'],
    },
  },
});

const attachedNuggetsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
  mt: backrs.spacing['spacing-sm'],
});

const attachedFilesMessageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-xs'],
  mt: backrs.spacing['spacing-sm'],
});

const attachedLinkSx: SxProps<Theme> = {
  cursor: 'pointer',
};
